import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Target,
  Eye,
  Compass,
  Heart,
  ExternalLink,
  Linkedin,
} from "lucide-react";
import api from "@/lib/api";
import { PageBanner } from "@/pages/Listings";
import { SectionHeading } from "@/components/Cards";
import { HeadquartersCards, SocialLinks } from "@/components/SiteIdentity";

const VALUES = [
  {
    icon: Compass,
    title: "Our Purpose",
    text: "To unite the global Dir community through a single, connected digital platform that strengthens collaboration and heritage.",
  },
  {
    icon: Eye,
    title: "Our Vision",
    text: "A thriving, connected global community empowered across the Horn and every continent.",
  },
  {
    icon: Target,
    title: "Our Mission",
    text: "To connect, empower, preserve and build — advancing opportunity for present and future generations.",
  },
  {
    icon: Heart,
    title: "Our Values",
    text: "Unity, integrity, heritage, service, excellence and shared prosperity guide everything we do.",
  },
];

export default function About() {
  const [leaders, setLeaders] = useState([]);
  const [partners, setPartners] = useState([]);
  useEffect(() => {
    api
      .get("/leadership")
      .then(({ data }) => setLeaders(data))
      .catch(() => {});
    api
      .get("/partners")
      .then(({ data }) => setPartners(data))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-dir-bg" data-testid="about-page">
      <PageBanner
        label="About DIR Nation"
        title="Who We Are"
        desc="A premier global institution connecting Dir people, communities and institutions across the world under one heritage."
      />

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
          <div className="grid gap-6 sm:grid-cols-2">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="rounded-2xl border border-dir-gold/15 bg-dir-card p-8"
                data-testid={`about-value-${i}`}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-dir-gold/30 bg-dir-gold/10">
                  <v.icon className="h-6 w-6 text-dir-gold" />
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold text-dir-text">
                  {v.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-dir-muted">
                  {v.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-dir-gold/15 bg-dir-deep py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
          <SectionHeading
            center
            label="Leadership"
            title="Our Leadership"
            desc="Dedicated leaders advancing unity, heritage and development worldwide."
          />
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[...leaders]
              .sort(
                (a, b) =>
                  (b.featured === true) - (a.featured === true) ||
                  (a.display_order ?? a.position ?? 0) -
                    (b.display_order ?? b.position ?? 0),
              )
              .map((l) => (
                <div
                  key={l.id}
                  className="overflow-hidden rounded-2xl border border-dir-gold/15 bg-dir-card"
                  data-testid={`leader-${l.id}`}
                >
                  {l.image_url || l.image ? (
                    <img
                      src={l.image_url || l.image}
                      alt={`${l.name} profile`}
                      className="aspect-[4/5] w-full object-cover"
                    />
                  ) : (
                    <div
                      className="flex aspect-[4/5] w-full items-center justify-center bg-dir-deep"
                      role="img"
                      aria-label={`${l.name} profile image placeholder`}
                    >
                      <span className="font-display text-4xl font-semibold text-dir-gold/70">
                        {(l.name || "DIR")
                          .split(/\s+/)
                          .slice(0, 2)
                          .map((part) => part[0])
                          .join("")}
                      </span>
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="font-display text-lg font-semibold text-dir-text">
                      {l.name}
                    </h3>
                    <p className="text-sm text-dir-gold">
                      {l.custom_role ||
                        l.role_title ||
                        l.title ||
                        "Leadership role to be confirmed"}
                    </p>
                    {l.country_region && (
                      <p className="mt-1 text-xs text-dir-faint">
                        {l.country_region}
                      </p>
                    )}
                    <p className="mt-3 text-sm text-dir-muted line-clamp-3">
                      {l.description || l.bio}
                    </p>
                    {(l.linkedin_url || l.url) && (
                      <a
                        href={l.linkedin_url || l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${l.name} on LinkedIn`}
                        className="mt-4 inline-flex min-h-11 min-w-11 items-center justify-center rounded-full border border-dir-gold/25 text-dir-gold hover:bg-dir-gold/10"
                      >
                        <Linkedin className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
          <SectionHeading
            center
            label="Strategic Collaboration"
            title="Working Together for Global Impact"
            desc="DIR Nation works with organizations and institutions that share a commitment to connecting communities, strengthening diaspora engagement, creating opportunities, and supporting sustainable development."
          />
          <div
            className={`mx-auto grid gap-6 ${partners.length === 1 ? "max-w-5xl" : partners.length === 2 ? "max-w-6xl md:grid-cols-2" : "max-w-[1400px] sm:grid-cols-2 lg:grid-cols-3"}`}
          >
            {partners.map((p) => (
              <article
                key={p.id}
                className={`overflow-hidden rounded-3xl border border-dir-gold/20 bg-dir-card shadow-xl shadow-black/10 ${partners.length === 1 ? "grid items-center md:grid-cols-[minmax(260px,0.8fr)_minmax(0,1.4fr)]" : "flex flex-col"}`}
                data-testid={`partner-${p.id}`}
              >
                <div className="flex min-h-64 items-center justify-center bg-dir-deep p-6 sm:p-8">
                  <img
                    src={p.image_url}
                    alt={`${p.name} official logo`}
                    className="h-auto max-h-72 w-full max-w-sm object-contain"
                  />
                </div>
                <div className="flex flex-col items-start p-7 sm:p-10">
                  {p.short_name && (
                    <span className="rounded-full border border-dir-gold/25 bg-dir-gold/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-dir-gold">
                      {p.short_name}
                    </span>
                  )}
                  <h3 className="mt-4 font-display text-2xl font-semibold text-dir-text sm:text-3xl">
                    {p.name}
                  </h3>
                  <p className="mt-4 max-w-2xl text-sm leading-7 text-dir-muted sm:text-base">
                    {p.description}
                  </p>
                  {p.url && (
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-6 inline-flex min-h-12 items-center gap-2 rounded-full border border-dir-gold/35 px-5 py-3 text-sm font-semibold text-dir-gold transition-colors hover:border-dir-gold hover:bg-dir-gold/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dir-gold"
                    >
                      Visit {p.short_name || p.name} Website{" "}
                      <ExternalLink className="h-4 w-4" aria-hidden="true" />
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="border-y border-dir-gold/15 bg-dir-deep py-16"><div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10"><SectionHeading label="Global presence" title="Headquarters & Regional Hubs" desc="DIR Nation connects its international, Horn of Africa and East Africa operations through three strategic hubs."/><HeadquartersCards/><SocialLinks location="about" className="mt-8"/></div></section>
    </div>
  );
}
